import {
  DropdownProps,
  MenuItemProps,
  MenuProps,
  Dropdown as MuiDropdown,
  Menu as MuiMenu,
  MenuButton as MuiMenuButton,
  MenuItem as MuiMenuItem,
  PopupProps,
  useMenuButton,
} from '@mui/base';
import { MenuButtonProps } from '@mui/base/MenuButton';
import cx from 'classnames';
import { ChevronDown, ChevronUp } from 'ethena-icons';
import { ElementType, forwardRef } from 'react';
import css from './DropdownMenu.module.scss';

interface IDropdownMenuProps extends DropdownProps {}

const DropdownMenu = (props: IDropdownMenuProps) => <MuiDropdown {...props} />;

interface IToggleProps extends MenuButtonProps {
  as?: ElementType;
  elementProps?: { [key: string]: any };
  icon?: ElementType;
  iconSize?: number;
}

const Toggle = ({
  as,
  children,
  className,
  elementProps,
  icon: passedIcon,
  iconSize = 16,
  ...props
}: IToggleProps) => {
  const { open: isOpen } = useMenuButton();

  const renderIcon = () => {
    let Icon: ElementType;
    if (!!passedIcon) {
      Icon = passedIcon;
    } else {
      Icon = isOpen ? ChevronUp : ChevronDown;
    }
    return (
      <Icon
        aria-hidden="true"
        className={css.dropdownMenuToggleIcon}
        height={iconSize}
        width={iconSize}
      />
    );
  };

  return (
    <MuiMenuButton
      {...props}
      className={cx(css.dropdownMenuToggle, className)}
      slots={{ root: as }}
      slotProps={{ root: elementProps }}
    >
      {children}
      {renderIcon()}
    </MuiMenuButton>
  );
};

interface IOptionsProps extends MenuProps {
  'aria-label': string;
  /**
   * `placement` is an undocumented prop in the MUI Menu component, inherited from `Popup`.
   * See: https://mui.com/base-ui/react-popup/
   */
  placement?: PopupProps['placement'];
}

const Options = forwardRef<HTMLDivElement, IOptionsProps>(
  (
    { 'aria-label': ariaLabel, children, className, placement, ...rest },
    ref
  ) => {
    return (
      <MuiMenu
        className={cx(css.dropdownMenuOptions, className)}
        // @ts-ignore
        placement={placement}
        ref={ref}
        slotProps={{
          listbox: { 'aria-label': ariaLabel },
        }}
        {...rest}
      >
        {children}
      </MuiMenu>
    );
  }
);

Options.displayName = 'DropdownMenu.Options';

interface IOptionProps extends MenuItemProps {
  as?: ElementType;
  /**
   * Props specific to the chosen element, such as href for a link.
   */
  elementProps?: { [key: string]: any };
  omitListItem?: boolean;
}

const Option = ({
  as,
  children,
  className,
  elementProps,
  omitListItem,
  ...props
}: IOptionProps) => {
  const renderCustomOption = () => (
    <MuiMenuItem
      {...props}
      className={cx(css.dropdownMenuOption, className)}
      slots={{ root: as }}
      slotProps={{ root: elementProps }}
    >
      {children}
    </MuiMenuItem>
  );

  const renderOption = () => (
    <MuiMenuItem {...props} className={cx(css.dropdownMenuOption, className)}>
      {children}
    </MuiMenuItem>
  );

  if (as) {
    if (omitListItem) {
      return renderCustomOption();
    }
    return <li>{renderCustomOption()}</li>;
  }

  return renderOption();
};

DropdownMenu.Options = Options;
DropdownMenu.Option = Option;
DropdownMenu.Toggle = Toggle;

export { DropdownMenu };
export type {
  IOptionProps as IDropdownMenuOptionProps,
  IOptionsProps as IDropdownMenuOptionsProps,
  IDropdownMenuProps,
  IToggleProps as IDropdownMenuToggleProps,
};
